/**
 * 작성자 : 박승호
 * 날짜 : 2024.08.23
 * 경로 : 센서관리 - 무선 유해가스 측정기
 */

import styled from 'styled-components';
import { ContentsContainerRoot } from '../../assets/styles/ContentsContainerRoot';
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { IUser, pageInfoState, userState } from '../../atoms';
import { todayYYYYMMDD } from '../../utils/formatDate';
import { useQuery } from '@tanstack/react-query';
import { RefObject, useEffect, useState, useMemo } from 'react';
import { IComCdList, ISelectBox, ISensorRange, ITabObject } from 'customTypes';
import { apiGet } from '../../services/_common';
import dayjs from 'dayjs';
import i18n from '../../translation/i18n';
import { useTranslation } from 'react-i18next';
import { setComCdListState } from '../../utils/setComCdListState';
import { COMCD_SENSOR_RANGE, LIST_COUNT, O2_RANGE, SENSOR_TIME } from '../../_constants';
import useOnKeydownF9 from '../../utils/useOnKeydownF9';
import { useSetAuth } from '../../utils/useSetAuth';
import { toast } from 'react-toastify';
import { logPost } from '../../services/log';
import { BtnGhost, BtnGray } from '../../components/Button';
import Tab from '../../components/Tab';
import { SearchOptions } from '../../assets/styles/SearchOptions';
import IssueGuide from '../../components/IssueGuide';
import SelectBox from '../../components/SelectBox';
import RangePicker, { onChangeRangeInput } from '../../components/RangePicker';
import ShortcutButton from '../../components/button/ShortcutButton';
import useSetListCount from '../../utils/useSetListCount';
import { LoadingModalBackground } from '../../assets/styles/Modal';
import TuiGridTable from '../../components/TuiGridTable';
import { PulseLoader } from 'react-spinners';
import illustrator from '../../assets/images/illustration/31.svg';
import nodataIllustrator from '../../assets/images/illustration/304.svg';
import Portal from '../../components/Portal';
import LineChartModal from '../../components/Modal/lineChartModal';
import BackButton from '../../components/BackButton';
import SearchFilterModal from '../../components/Modal/SearchFilterModal';
import { showExcelButton } from '../../utils/deviceAuth';
import { convertCO2PpmToPercent, convertCO2PpmToMgPerCubicMeter } from '../../utils/convertCO2Unit';

const Root = styled(ContentsContainerRoot)`
  --good: var(--primary);
  --caution: var(--filled-green);
  --warning: var(--filled-amber);
  --danger: var(--filled-red);

  width: 100%;

  &.headerLeft {
    tbody {
      tr {
        th:last-child {
          text-align: left !important;
        }
      }
    }
  }
  &.hideRoot {
    visibility: hidden;
    position: absolute;
  }
  .tui-container {
    flex-grow: 1;
    overflow: hidden;
    margin-left: 0.5rem;
  }
  .loader {
    display: flex;
    width: 100%;
    height: 100%;
  }
  .emptyData {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    user-select: none;
    padding-bottom: 4rem;
    gap: 1rem;
    img {
      width: 14rem;
      /* user-drag: none; */
      -webkit-user-drag: none;
    }
    span {
      color: var(--filled-violet);
    }
  }
  .inputFormsWrapper {
    background-color: var(--board);
    border-radius: 0.5rem;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
  .searchContent {
    display: flex;
    justify-content: space-between;
    .buttonsWrapper {
      display: flex;
      margin-bottom: 0rem;
    }
  }

  .end {
    justify-content: end;
    > button {
      margin-left: 1rem;
    }
  }

  .thead > .tr {
    > div {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .spaceBetween {
      justify-content: space-evenly;
    }
  }

  .table {
    height: 64rem;
    padding: 0;

    > .tbody > .tr {
      padding: 0.4rem 1rem;
    }
  }

  .tab {
    padding: 0 1rem;
    cursor: pointer;
    height: 3rem;
  }

  .activeTab {
    font-weight: bold;
    border-bottom: 2px solid rgb(63, 66, 84);
  }

  .marginBottomNone {
    margin-bottom: 0;
  }

  .tui-grid-filter-dropdown select {
    box-sizing: border-box;
  }

  @media screen and (max-width: 767px) {
    .searchContent {
      flex-direction: column;
      > div {
        width: 100% !important;
      }
    }
  }
  .range-wrapper {
    width: 420px;

    .range-top {
      display: flex;
      align-items: center;
      justify-content: center;
      margin-left: -8px;

      .range-status {
        display: flex;
        align-items: center;
        font-size: 0.9rem;
        width: 72px;
        margin-right: 12px;

        &::before {
          content: '●';
          font-size: 1.1rem;
          margin-right: 4px;
        }

        &:nth-child(1)::before {
          color: var(--good);
        }
        &:nth-child(2)::before {
          color: var(--caution);
        }
        &:nth-child(3)::before {
          color: var(--warning);
        }
        &:nth-child(4) {
          margin-right: 0;

          &::before {
            color: var(--danger);
          }
        }
      }
    }
    .range-bottom {
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;

      .range-left {
        display: flex;
        font-size: 14px;
        color: var(--text-tertiary);
      }

      .range-center {
        display: flex;
        align-items: center;

        .range-guage-item {
          width: 72px;
          font-size: 0.925rem;
          margin-right: 12px;

          hr {
            border: none;
            height: 1px;
          }

          .guage-item-top {
            display: flex;
            justify-content: space-between;
          }
          .guage-item-bottom {
            display: flex;
            justify-content: space-between;
          }
          &:nth-child(1) {
            color: var(--good);
            hr {
              background: var(--good);
            }
          }

          &:nth-child(2) {
            color: var(--caution);
            hr {
              background: var(--caution);
            }
          }

          &:nth-child(3) {
            color: var(--warning);
            hr {
              background: var(--warning);
            }
          }

          &:nth-child(4) {
            color: var(--danger);
            margin-right: 0;
            hr {
              background: var(--danger);
            }
          }
        }
      }

      .range-right {
        font-size: 14px;
        color: var(--text-tertiary);
      }
    }
  }
  .value {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 0.25rem;
    &::before {
      content: '●';
      font-size: 1.5rem;
      padding-top: 2px;
    }
    &.level {
      .value-title {
        color: var(--text-tertiary);
      }
      &::before {
        color: transparent;
      }
    }
    &.level0 {
      .value-title {
        color: var(--good);
      }
      &::before {
        color: var(--good);
      }
    }
    &.level1 {
      .value-title {
        color: var(--caution);
      }
      &::before {
        color: var(--caution);
      }
    }
    &.level2 {
      .value-title {
        color: var(--warning);
      }
      &::before {
        color: var(--warning);
      }
    }
    &.level3 {
      .value-title {
        color: var(--danger);
      }
      &::before {
        color: var(--danger);
      }
    }
  }

  .secondSearchOption .guide.levels {
    flex-shrink: 0;
    display: flex;
    gap: 1rem;
    font-size: 0.875rem;
    margin: 0 0.5rem;
    @media (min-width: 768px) {
      margin-right: 1rem;
    }
    > div.value {
      > div {
        > span {
          line-height: 1rem;
          &:nth-child(2) {
            color: var(--text-tertiary);
          }
        }
      }
    }
  }

  .mgas-responsive {
    flex-wrap: nowrap;
    @media (min-width: 1025px) {
      flex-wrap: wrap;
      overflow: visible !important;
    }
  }
`;

const Ssensor8 = () => {
  const location = useLocation();
  const tabInfo = useRecoilValue(pageInfoState);
  const toDay = todayYYYYMMDD();
  const userInfo = useRecoilValue<IUser>(userState);
  const { hCd, sCd } = userInfo;
  const { defaultMrCd } = useRecoilValue(pageInfoState);
  const size = useOutletContext<any>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const INIT_F_NAME = { type: 'fName', fName: 'A', cdName: t('전체') };
  const { auth } = useSetAuth(); // 사용자 권한값 훅
  const { isF9Pressed, setIsF9Pressed } = useOnKeydownF9(); // f9키 프레스 훅
  const { listCount, setListCount, patchUserMenuAPI } = useSetListCount(); // 검색줄수설정 훅
  const { userMenuList } = userInfo;

  const [rowState, setRowState] = useState<any>({});
  const [viewRowState, setViewRowState] = useState<boolean>(false);
  const [exportTableState, setExportTableState] = useState<any[]>([]);
  const [mainColumns, setMainColumns] = useState<any[]>([]);
  const [subColumns, setSubColumns] = useState<any[]>([]);
  const [exportColumns, setExportColumns] = useState<any[]>([]);
  const [currentTabMrCd, setCurrentTabMrCd] = useState(defaultMrCd);
  const [visibleRangePicker, setVisibleRangePicker] = useState(false);
  const [listCountComCdList, setListCountComCdList] = useState<IComCdList[]>([]); // 검색줄수 공통코드
  const [fNameList, setFnameList] = useState<any[]>([]);
  const [fName, setFName] = useState(INIT_F_NAME);
  const [excelBtn, setExcelBtn] = useState(false);
  const [excelBtn2, setExcelBtn2] = useState(false);
  const [mainChartDataReverse, setMainChartDataReverse] = useState<any>([]);
  const [subChartDataReverse, setSubChartDataReverse] = useState<any>([]);
  const [openModal, setOpenModal] = useState<any>({ status: false, type: '' });
  const [newTabList, setNewTabList] = useState<ITabObject[]>([]);
  const [mGasInfo, setMGasInfo] = useState({ type: '1', name: t('산소'), tableKey: { value: 'O2', valueType: '% vol' } });
  const [searchOption, setSearchOption] = useState({ start: toDay, end: toDay });
  const [sensorTime, setSensorTime] = useState<ISelectBox>({ type: 'time', time: '1', cdName: t('시간') });
  const [rangeState, setRangeState] = useState([{ startDate: new Date(), endDate: new Date(), key: 'selection' }]);
  const showExcelBtn = showExcelButton(auth.excelAuth);

  // CO2 단위(unit)값에 따라 헤더 표기(valueType) 변환 (CO2에만 적용)
  const getCO2ValueType = (unit?: string) => {
    switch (unit) {
      case '%':
        return '%';
      case 'mg/m³':
        return 'mg/m³';
      default:
        return 'PPM';
    }
  };

  // CO2 ppm 값을 unit에 따라 변환하는 함수
  const convertCO2Value = (ppmValue: number | string | undefined, tabMrCd?: string): number => {
    if (ppmValue === undefined || ppmValue === null || ppmValue === '') {
      return 0;
    }
    const numValue = typeof ppmValue === 'string' ? Number(ppmValue) : ppmValue;
    if (isNaN(numValue)) {
      return 0;
    }

    // 현재 탭 정보 확인
    const currentTabInfo = tabMrCd ? getMgasInfo(tabMrCd) : mGasInfo;

    // CO2 탭이 아니면 그대로 반환
    if (currentTabInfo.tableKey.value !== 'CO2') {
      return numValue;
    }

    // allSensorRanges에서 현재 탭의 range 정보 가져오기
    let currentRange = null;
    if (tabMrCd && allSensorRanges) {
      // tabMrCd가 전달되면 allSensorRanges에서 직접 가져오기
      const currentTabInfoForRange = getMgasInfo(tabMrCd);
      currentRange = allSensorRanges[currentTabInfoForRange.tableKey.value];
    } else {
      // tabMrCd가 없으면 현재 sensorRange 사용
      currentRange = sensorRange;
    }

    if (!currentRange?.unit) {
      return numValue;
    }

    const { unit } = currentRange;
    switch (unit) {
      case '%':
        return convertCO2PpmToPercent(numValue);
      case 'mg/m³':
      case 'mg':
        return convertCO2PpmToMgPerCubicMeter(numValue);
      case 'ppm':
      default:
        return numValue;
    }
  };

  // type 값을 센서 코드로 변환하는 함수
  const getSensorCodeByType = (type: string): string => {
    const typeMap: { [key: string]: string } = {
      '1': 'O2',
      '2': 'NO2',
      '3': 'CO',
      '4': 'CO2',
      '5': 'H2S',
      '6': 'CH4',
      '7': 'CH2O',
      '8': 'O3',
      '9': 'PM2.5',
      '10': 'PM10',
      '11': 'TEMP',
      '12': 'HUMI',
      '13': 'VOC',
      '14': 'PM1',
      '15': 'FEELS',
    };
    return typeMap[type] || '';
  };

  // 현재 탭에 type과 name, table에 사용하는 key값 구하는 함수
  const getMgasInfo = (mrCd: string) => {
    switch (mrCd) {
      case '001':
        return { type: '1', name: t('산소'), tableKey: { value: 'O2', valueType: '% vol' } };
      case '002':
        return { type: '2', name: t('이산화질소'), tableKey: { value: 'NO2', valueType: 'PPM' } };
      case '003':
        return { type: '3', name: t('일산화탄소'), tableKey: { value: 'CO', valueType: 'PPM' } };
      case '004':
        return { type: '4', name: t('이산화탄소'), tableKey: { value: 'CO2', valueType: 'PPM' } };
      case '005':
        return { type: '5', name: t('황화수소'), tableKey: { value: 'H2S', valueType: 'PPM' } };
      case '006':
        return { type: '6', name: t('메탄'), tableKey: { value: 'CH4', valueType: '% LEL' } };
      case '007':
        return { type: '7', name: t('폼알데하이드'), tableKey: { value: 'CH2O', valueType: 'mg/m³' } };
      case '008':
        return { type: '8', name: t('오존'), tableKey: { value: 'O3', valueType: 'PPM' } };
      case '009':
        return { type: '9', name: t('초미세먼지'), tableKey: { value: 'PM2.5', valueType: 'µg/m³' } };
      case '010':
        return { type: '10', name: t('미세먼지'), tableKey: { value: 'PM10', valueType: 'µg/m³' } };
      case '011':
        return { type: '11', name: t('온도'), tableKey: { value: 'TEMP', valueType: '°C' } };
      case '012':
        return { type: '12', name: t('습도'), tableKey: { value: 'HUMI', valueType: '%' } };
      case '013':
        return { type: '13', name: t('유기화합물질'), tableKey: { value: 'VOC', valueType: 'Grade' } };
      case '014':
        return { type: '14', name: t('극초미세먼지'), tableKey: { value: 'PM1', valueType: 'µg/m³' } };
      case '015':
        return { type: '15', name: t('체감온도'), tableKey: { value: 'FEELS', valueType: '°C' } };
      default:
        return { type: '1', name: t('산소'), tableKey: { value: 'O2', valueType: '% vol' } };
    }
  };

  // 모든 센서의 range 정보를 가져오는 쿼리
  const {
    data: allSensorRanges,
    isLoading: isLoadingAllSensorRanges,
    isFetching: isFetchingAllSensorRanges,
    isError: isErrorAllSensorRanges,
  } = useQuery([`allSensorRangesGet`, hCd, sCd], () => fetchAllSensorRanges(), { enabled: !!hCd && !!sCd, cacheTime: 0 });

  // 현재 탭의 센서 range 정보를 allSensorRanges에서 추출
  const sensorRange = useMemo(() => {
    if (!allSensorRanges || !mGasInfo.tableKey.value) return null;
    const range = allSensorRanges[mGasInfo.tableKey.value];
    if (!range) return null;
    return {
      level1: range.level1,
      level2: range.level2,
      level3: range.level3,
      srCd: mGasInfo.tableKey.value,
      unit: range.unit,
    };
  }, [allSensorRanges, mGasInfo.tableKey.value]);

  const fetchAllSensorRanges = async () => {
    try {
      const res = await apiGet({ path: '/sensor/range', req: { hCd, sCd } });
      const rangeMap: { [key: string]: { level1: number; level2: number; level3: number; unit?: string } } = {};
      res.data.data.rangeList.forEach((list: ISensorRange) => {
        rangeMap[list.srCd] = {
          level1: list.level1 ? Number(list.level1) : 0,
          level2: list.level2 ? Number(list.level2) : 0,
          level3: list.level3 ? Number(list.level3) : 0,
          unit: list.unit,
        };
      });
      return rangeMap;
    } catch (error) {
      console.error(error);
      throw new Error('error');
    }
  };

  const mainDataSet = [
    {
      label: t('평균'),
      data: `avgData${getMgasInfo(currentTabMrCd).type}`,
      borderColor: 'rgba(0, 221, 255, 0.5)',
      backgroundColor: 'rgba(0, 221, 255, 0.5)',
    },
    {
      label: t('최소'),
      data: `minData${getMgasInfo(currentTabMrCd).type}`,
      borderColor: 'rgba(0, 119, 255, 0.5)',
      backgroundColor: 'rgba(0, 119, 255, 0.5)',
    },
    {
      label: t('최대'),
      data: `maxData${getMgasInfo(currentTabMrCd).type}`,
      borderColor: 'rgba(0, 0, 255, 0.5)',
      backgroundColor: 'rgba(0, 0, 255, 0.5)',
    },
  ];

  const subDataSet = [
    {
      label: t(mGasInfo.name),
      data: `data${mGasInfo.type}`,
      borderColor: 'rgba(0, 221, 255, 0.5)',
      backgroundColor: 'rgba(0, 221, 255, 0.5)',
    },
  ];

  useEffect(() => {
    const arr: any[] = [];
    userMenuList.map((v: any) => {
      const result = tabInfo?.tabList.find((el: ITabObject) => el.mtCd === v.mtCd && el.mdCd === v.mdCd && el.mrCd === v.mrCd);
      if (result) {
        arr.push(result);
        setNewTabList(arr);
      }
    });
  }, [tabInfo.tabList]);

  useEffect(() => {
    if (!rangeState[0].startDate) {
      return;
    }
    setSearchOption((prev: any) => ({ ...prev, start: dayjs(rangeState[0].startDate).format('YYYY-MM-DD') }));
  }, [rangeState[0].startDate]);

  useEffect(() => {
    if (!rangeState[0].endDate) {
      return;
    }
    setSearchOption((prev: any) => ({ ...prev, end: dayjs(rangeState[0].endDate).format('YYYY-MM-DD') }));
  }, [rangeState[0].endDate]);

  const sensorValueFormatter = (value: any) => {
    const numValue = Number(value);
    const localeStringValue = !isNaN(numValue) ? numValue.toLocaleString() : '정보없음';
    return localeStringValue;
  };

  useEffect(() => {
    const headerValueType = mGasInfo.tableKey.value === 'CO2' ? getCO2ValueType(sensorRange?.unit) : mGasInfo.tableKey.valueType;

    const defaultExportColumns = [
      {
        header: t('장비명'),
        name: 'dvName',
        align: 'left',
        sortable: true,
        width: 120,
        renderer: { classNames: ['text_secondary'] },
      },
      {
        header: t('측정일자/시간'),
        name: 'checkTime',
        align: 'center',
        sortable: true,
        minWidth: 140,
        renderer: { classNames: ['text_secondary'] },
      },
      {
        header: `${headerValueType}`,
        name: `data${mGasInfo.type}`,
        align: 'center',
        sortable: true,
        filter: 'number',
        minWidth: 120,
      },
    ];
    setMainColumns([
      {
        header: t('장비명'),
        name: 'dvName',
        align: 'left',
        sortable: true,
        filter: 'select',
        width: 120,
        renderer: { classNames: ['text_secondary'] },
      },
      {
        header: t('측정일자'),
        name: 'checkTime',
        align: 'center',
        sortable: true,
        filter: 'select',
        minWidth: 120,
        renderer: { classNames: ['text_secondary'] },
      },
      {
        header: t('평균'),
        name: `avgData${mGasInfo.type}`,
        align: 'center',
        sortable: true,
        filter: 'number',
        minWidth: 120,
      },
      {
        header: t('최소'),
        name: `minData${mGasInfo.type}`,
        align: 'center',
        sortable: true,
        filter: 'number',
        minWidth: 120,
      },
      {
        header: t('최대'),
        name: `maxData${mGasInfo.type}`,
        align: 'center',
        sortable: true,
        filter: 'number',
        minWidth: 120,
      },
    ]);
    setSubColumns([
      {
        header: t('장비명'),
        name: 'dvName',
        align: 'left',
        sortable: true,
        width: 120,
        renderer: { classNames: ['text_secondary'] },
      },
      {
        header: t('측정일자/시간'),
        name: 'checkTime',
        align: 'center',
        sortable: true,
        minWidth: 140,
        renderer: { classNames: ['text_secondary'] },
      },
      {
        header: `${headerValueType}`,
        name: `data${mGasInfo.type}_color`,
        align: 'center',
        sortable: false,
        width: 40,
      },
      {
        header: ` `,
        name: `data${mGasInfo.type}`,
        align: 'left',
        sortable: true,
        filter: 'number',
        width: 120,
        formatter: ({ value }: { value: string }) => sensorValueFormatter(value),
      },
    ]);

    // subtable에서 보기 설정에 따라 엑셀 다운로드시 상태 값 넣어줄지 뺄지 정하는 로직
    if (sensorTime.time !== '1') {
      setExportColumns([
        ...defaultExportColumns,
        {
          header: t('상태'),
          name: `data${mGasInfo.type}_status`,
          align: 'center',
          sortable: true,
          filter: 'number',
          minWidth: 120,
        },
      ]);
    } else {
      setExportColumns([...defaultExportColumns]);
    }
  }, [i18n.language, mGasInfo.type, mGasInfo.tableKey.value, sensorRange?.unit, sensorTime.time]);

  const {
    data: mainSensor,
    isLoading: isLoadingMainSensor,
    isFetching: isFetchingMainSensor,
    isError: isErrorMainSensor,
    refetch: refetchMainSensor,
  } = useQuery([`mainSensorGet_${currentTabMrCd}`, currentTabMrCd], () => fetchMainSensorData(), {
    enabled: !!hCd && !!sCd,
    cacheTime: 0,
  });

  const fetchMainSensorData = async () => {
    try {
      const { start, end } = searchOption;
      const req = {
        hCd,
        sCd,
        dvNo: fName.fName === 'A' ? '' : fName.fName,
        date1: start.replaceAll('-', ''),
        date2: end.replaceAll('-', ''),
        type: getMgasInfo(currentTabMrCd).type,
      };
      const res = await apiGet({ path: '/sensor/wgas', req });
      // CO2인 경우 변환된 값 적용
      const processedData = res.data.data.map((element: any) => {
        const result = { ...element };
        const currentTabInfo = getMgasInfo(currentTabMrCd);
        if (currentTabInfo.tableKey.value === 'CO2') {
          const { type } = currentTabInfo;
          if (result[`avgData${type}`] !== undefined) {
            result[`avgData${type}`] = convertCO2Value(result[`avgData${type}`], currentTabMrCd);
          }
          if (result[`maxData${type}`] !== undefined) {
            result[`maxData${type}`] = convertCO2Value(result[`maxData${type}`], currentTabMrCd);
          }
          if (result[`minData${type}`] !== undefined) {
            result[`minData${type}`] = convertCO2Value(result[`minData${type}`], currentTabMrCd);
          }
        }
        return result;
      });
      const dvList = toDvList(processedData);
      const newData = dvList.map((v: any) => {
        return processedData.filter((v2: any) => v2.dvNo === v.dvNo);
      });
      setMainChartDataReverse(newData.map((v3: any) => v3.reverse()));

      logPost({
        hCd,
        sCd,
        userId: userInfo.userId,
        menu: `${mGasInfo.name} 측정기`,
        action: '조회',
        etc: ``,
      });

      return processedData;
    } catch (error) {
      console.error(error);
      throw new Error('error');
    }
  };

  const {
    data: subSensor,
    isLoading: isLoadingSubSensor,
    isFetching: isFetchingSubSensor,
    isError: isErrorSubSensor,
    refetch: refetchSubSensor,
  } = useQuery([`subSensorGet_${sensorTime.time}_${rowState.rowKey}`, sensorTime.time, rowState.rowKey], () => fetchSubSensorData(), {
    enabled: !!hCd && !!sCd,
  });

  const fetchSubSensorData = async () => {
    if (rowState.rowKey === undefined) return [];
    try {
      const req = {
        hCd,
        sCd,
        dvNo: rowState.dvNo,
        date: rowState.checkTime?.replaceAll('-', ''),
        time: sensorTime.time,
        type: mGasInfo.type,
      };
      const res = await apiGet({ path: '/sensor/wgasDay', req });

      if (res.status === 200) {
        if (sensorTime.time === '1') {
          // CO2인 경우 변환된 값 적용
          const currentTabInfo = getMgasInfo(currentTabMrCd);
          const processedData = res.data.data.map((v: any) => {
            const result = { ...v, checkTime: `${v.checkTime}${t('시')}` };
            if (currentTabInfo.tableKey.value === 'CO2' && v.data4 !== undefined) {
              result.data4 = convertCO2Value(v.data4, currentTabMrCd);
            }
            return result;
          });
          setExportTableState(processedData);
          const reversedData = [...res.data.data].reverse();
          setSubChartDataReverse([reversedData]);
          return processedData;
        }

        const currentTabInfo = getMgasInfo(currentTabMrCd);
        const formatted = res.data.data.map((v: any) => {
          const result: any = { ...v };

          for (let i = 1; i <= 20; i += 1) {
            // CO2 (type 4 = data4)인 경우 변환된 값 사용
            let dataValue = v[`data${i}`];
            if (i === 4 && currentTabInfo.tableKey.value === 'CO2') {
              dataValue = convertCO2Value(v[`data${i}`], currentTabMrCd);
            }
            // level 계산
            const calculatedLevel = calculateLevel(String(i), v[`data${i}`], currentTabMrCd);
            result[`data${i}_status`] = getStatus(dataValue, calculatedLevel);
            result[`data${i}`] = sensorValueFormatter(dataValue);
          }

          return result;
        });

        setExportTableState(formatted);
        // 차트에는 CO2 변환된 데이터 사용
        const chartData = res.data.data.map((v: any) => {
          const entry: any = { ...v };
          for (let i = 1; i <= 20; i += 1) {
            // CO2 (type 4 = data4)인 경우 변환된 값 사용
            if (i === 4 && currentTabInfo.tableKey.value === 'CO2') {
              entry[`data${i}`] = convertCO2Value(v[`data${i}`], currentTabMrCd);
            }
          }
          return entry;
        });
        setSubChartDataReverse([chartData.reverse()]);

        const result = res.data.data.map((v: any) => {
          const entry: any = { ...v };

          for (let i = 1; i <= 20; i += 1) {
            const data = v[`data${i}`];
            // level 계산
            const calculatedLevel = calculateLevel(String(i), data, currentTabMrCd);
            entry[`data${i}_color`] = `<span class='value ${getColor(data, calculatedLevel)}' />`;
            // CO2 (type 4 = data4)인 경우 변환된 값 사용
            if (i === 4 && currentTabInfo.tableKey.value === 'CO2') {
              entry[`data${i}`] = convertCO2Value(data, currentTabMrCd);
            } else {
              entry[`data${i}`] = data;
            }
          }

          return entry;
        });

        return result;
      }
      return [];
    } catch (error) {
      console.error(error);
      throw new Error('error');
    }
  };

  useEffect(() => {
    setMGasInfo(getMgasInfo(currentTabMrCd));
  }, [currentTabMrCd]);

  useEffect(() => {
    getNormalCdAPI();
    setComCdListState(LIST_COUNT, setListCountComCdList, false);
  }, []);

  useEffect(() => {
    if (isF9Pressed) {
      onClickSearch();
      setIsF9Pressed(false);
    }
  }, [isF9Pressed]);

  useEffect(() => {
    if (rowState) refetchSubSensor();
  }, [rowState.rowKey, sensorTime]);

  // 장비 리스트 조회 API
  const getNormalCdAPI = async () => {
    const req = { hCd, sCd, grCd: '09' };
    const res = await apiGet({ path: '/sensor/detail', req });
    const { data, statusCode } = res.data !== undefined && res.data;
    if (statusCode === 200) {
      const newList = [];
      newList.push(INIT_F_NAME);
      data.sensorList.map((v: any) => {
        newList.push({ type: 'fName', fName: v.subCd, cdName: v.sName });
      });
      setFnameList(newList);
    } else {
      // toast.error(t(ERROR));
    }
  };

  // 센서 값과 range를 비교하여 level을 계산하는 함수
  const calculateLevel = (type: string, sensorValue: number | string | undefined, tabMrCd?: string): string => {
    if (!allSensorRanges || sensorValue === undefined || sensorValue === null || sensorValue === '') {
      return '';
    }

    const numValue = typeof sensorValue === 'string' ? Number(sensorValue) : sensorValue;
    if (isNaN(numValue)) {
      return '';
    }

    const sensorCode = getSensorCodeByType(type);
    if (!sensorCode) {
      return '';
    }

    const range = allSensorRanges[sensorCode];
    if (!range || !range.level1 || !range.level2 || !range.level3) {
      return '';
    }

    const { level1, level2, level3 } = range;
    const valueToCompare = numValue;

    // 산소 경우 범위 계산
    if (sensorCode === 'O2') {
      const matchedRange = [...O2_RANGE].reverse().find((o2Range) => {
        const [lowerMin, lowerMax] = o2Range.lower;
        const [upperMin, upperMax] = o2Range.upper;

        // 하한선(lower) 체크: -1은 무시, 값이 범위 내에 있는지 확인
        const isLowerBreached = (lowerMin !== -1 && valueToCompare < lowerMin) || (lowerMax !== -1 && valueToCompare <= lowerMax);

        // 상한선(upper) 체크: -1은 무시
        const isUpperBreached = (upperMin !== -1 && valueToCompare > upperMin) || (upperMax !== -1 && valueToCompare >= upperMax);

        return isLowerBreached || isUpperBreached;
      });

      return matchedRange ? String(matchedRange.level) : '0';
    }

    // CO2의 경우 변환된 값으로 비교
    if (sensorCode === 'CO2' && range.unit) {
      const co2Value = convertCO2Value(numValue);
      if (co2Value < level1) return '0';
      if (co2Value >= level1 && co2Value < level2) return '1';
      if (co2Value >= level2 && co2Value < level3) return '2';
      return '3';
    }

    // level0 (좋음): 값 < level1
    // level1 (주의): level1 <= 값 < level2
    // level2 (경고): level2 <= 값 < level3
    // level3 (위험): level3 <= 값
    if (valueToCompare < level1) {
      return '0';
    }
    if (valueToCompare >= level1 && valueToCompare < level2) {
      return '1';
    }
    if (valueToCompare >= level2 && valueToCompare < level3) {
      return '2';
    }
    return '3';
  };

  const getColor = (value: string, lv: string) => {
    const val = Number(value);
    if (val >= 0) {
      if (lv === '0') return 'level0';
      if (lv === '1') return 'level1';
      if (lv === '2') return 'level2';
      if (lv === '3') return 'level3';
    }
    return '';
  };

  const getStatus = (value: string, lv: string) => {
    const val = Number(value);
    if (val >= 0) {
      if (lv === '0') return t('좋음');
      if (lv === '1') return t('주의');
      if (lv === '2') return t('경고');
      if (lv === '3') return t('위험');
    }
    return '-';
  };

  const toDvList = (data: any[]) => {
    const map = new Map(data.map((m: any) => [m.dvNo, m])).entries();
    return Array.from(map).map((v: any) => {
      return { dvNo: v[1].dvNo, dvName: v[1].dvName };
    });
  };

  // 검색 버튼 클릭
  const onClickSearch = (filterRef?: RefObject<HTMLDivElement>) => {
    if (!openModal.status || (openModal.status && openModal.type === 'filter')) {
      if (searchOption.end && searchOption.start > searchOption.end) {
        toast.warning(t('날짜를 다시 입력하세요'));
        setSearchOption((prev: any) => ({ ...prev, end: '' }));
      } else {
        if (filterRef) {
          // 모바일일 경우 검색 필터 모달 창 닫기
          filterRef.current?.classList.remove('active');
          setOpenModal({ status: false, type: 'filter' });
        }
        refetchMainSensor();
        setViewRowState(false);
        setVisibleRangePicker(false);
        navigate({ pathname: location.pathname, search: '' });
      }
    }
  };

  // 초기화 버튼 클릭
  const onClickInitiateSearchOption = () => {
    Object.keys(searchOption).map((el: any) => {
      return setSearchOption((prev: any) => ({ ...prev, [el]: '', start: toDay, end: toDay }));
    });
    setFName(INIT_F_NAME);
    setRangeState([{ startDate: new Date(), endDate: new Date(), key: 'selection' }]);
    setViewRowState(false);
  };

  // 차트 버튼 클릭
  const onClickChart = ({ tableData, type }: { tableData: any[]; type: any }) => {
    if (tableData.length !== 0) setOpenModal({ status: true, type });
    else toast.warning(t('데이터가 없습니다'));
  };

  // 엑셀 저장 버튼 클릭
  const onClickExcelSave = () => {
    if (mainSensor.length <= 0) return;
    setExcelBtn(true);

    logPost({
      hCd,
      sCd,
      userId: userInfo.userId,
      menu: `${mGasInfo.name} 측정기`,
      action: '엑셀 저장',
      etc: `${searchOption.start}~${searchOption.end}`,
    });
  };

  // 엑셀 저장 버튼 클릭
  const onClickExcelSave2 = () => {
    if (subSensor?.length <= 0) return;
    setExcelBtn2(true);
    logPost({
      hCd,
      sCd,
      userId: userInfo.userId,
      menu: `${mGasInfo.name} 측정기`,
      action: '엑셀 저장',
      etc: `${rowState.checkTime}`,
    });
  };

  // 엑셀 버튼
  const renderExcelBtn = (onClickBtn: any) => {
    if (showExcelBtn) {
      return (
        <BtnGhost onClick={onClickBtn}>
          <span className='icon-ms-xlsx'>X</span> {t('엑셀')}
        </BtnGhost>
      );
    }
    return null;
  };

  // 프린트 버튼
  const renderPrintBtn = (clickPrint: any) => {
    if (auth.printAuth) {
      return <BtnGray onClick={clickPrint}>{t('인쇄')}</BtnGray>;
    }
    return null;
  };

  // 측정기 Row 클릭
  const onClickRow = async (rowData: any): Promise<void> => {
    if (rowData) {
      setRowState(rowData);
      setViewRowState(true);
    }
  };

  const onClickRangeInput = () => {
    if (!visibleRangePicker) setVisibleRangePicker(true);
  };

  const onClickTab = (tab: string) => {
    setSensorTime({
      type: 'time',
      time: '1',
      cdName: t('시간'),
    });
    setViewRowState(false);
    setRowState({});
    setCurrentTabMrCd(tab);
  };

  // 필터 버튼 클릭
  const onClickFilter = () => {
    setOpenModal({ status: true, type: 'filter' });
  };

  const getFileName = (tableType: 'main' | 'sub') => {
    const type = mGasInfo.name;
    const detail = sensorTime.time === '1' ? t('시간') : t('상세');
    const startDate = rangeState[0].startDate ? dayjs(rangeState[0].startDate).format('YYYY-MM-DD') : '';
    const endDate = dayjs(rangeState[0].endDate).format('YYYY-MM-DD');
    const period = startDate !== '' ? (startDate === endDate ? startDate : `${startDate}~${endDate}`) : t('전체기간');

    if (tableType === 'sub') return `${type}(${detail})_${rowState.checkTime}`;
    return `${type}(${t('종합')})_${period}`;
  };

  const renderSubTable = () => {
    if (isErrorAllSensorRanges || isErrorSubSensor) return <IssueGuide />;

    // loading 중일때
    if (isLoadingAllSensorRanges || isFetchingAllSensorRanges) {
      return (
        <Root loading={isLoadingAllSensorRanges || isFetchingAllSensorRanges}>
          <div className='loader'>
            <PulseLoader className='flex-center' color='rgb(0, 122, 255)' size='1rem' style={{ width: 'inherit', height: 'inherit' }} />
          </div>
        </Root>
      );
    }

    // 브라우저 가로폭이 1024보다 작거나 viewrowstate가 false일때
    if (!(size.innerSize.W > 1024 || viewRowState)) {
      return null;
    }

    // viewrowstate가 false이고 왼쪽 데이터가 있을때
    if (!viewRowState) {
      return (
        <Root loading={false}>
          <div className='emptyData'>
            <img src={illustrator} alt='noData' />
            <span>{t('왼쪽 목록에서 센서를 선택해주세요')}</span>
          </div>
        </Root>
      );
    }

    // 브라우저 가로폭이 1024보다 크거나 viewrowstate가 true일때
    return (
      <Root loading={isLoadingSubSensor || isFetchingSubSensor} className='headerLeft'>
        <SearchOptions align='left'>
          <div className='inputsWrapperGroup'>
            {size.innerSize.W <= 1024 && <BackButton func={() => setViewRowState(false)} />}
            <div className='inputsWrapper sensor-results'>
              <div className='sensor-result-header'>
                <span>{t('측정일')}</span>
                <div>{rowState.checkTime}</div>
              </div>
              <div className='sensor-result-data'>
                <span>{t(mGasInfo.name)}</span>
                <div>
                  <span>{t('평균')}</span>
                  {rowState[`avgData${mGasInfo.type}`] || ''}
                </div>
                <div>
                  <span>{t('최소')}</span>
                  {rowState[`minData${mGasInfo.type}`] || ''}
                </div>
                <div>
                  <span>{t('최대')}</span>
                  {rowState[`maxData${mGasInfo.type}`] || ''}
                </div>
              </div>
            </div>
          </div>
          <div className='inputsWrapper sensorOverflow'>
            <div className='secondSearchOption mgas-responsive'>
              <div className='flex-basic shrink-0'>
                <SelectBox options={SENSOR_TIME} defaultOption={sensorTime.cdName} state={sensorTime} setState={setSensorTime} stateKey='time' />
              </div>
              {sensorTime.time !== '1' && (
                <div>
                  {sensorRange?.srCd === 'O2' ? (
                    <div className='flex-basic flex-col range-wrapper'>
                      <div className='range-top'>
                        <div className='range-status'>{t('좋음')}</div>
                        <div className='range-status'>{t('주의')}</div>
                        <div className='range-status'>{t('경고')}</div>
                        <div className='range-status'>{t('위험')}</div>
                      </div>
                      <div className='range-bottom'>
                        <div className='range-left flex-col'>
                          <span>{t('상한가')}</span>
                          <span>{t('하한가')}</span>
                        </div>
                        <div className='range-center'>
                          {O2_RANGE.map((item: { level: number; lower: number[]; upper: number[] }) => (
                            <div className='range-guage-item' key={item.level}>
                              <div className='guage-item-top'>
                                <span>{item.level !== 3 ? (item.upper[0] !== -1 ? item.upper[0] : '~') : `${item.upper[0]} ${t('초과')}`}</span>
                                {item.level !== 0 && item.level !== 3 && <span>~</span>}
                                <span>{item.level !== 0 ? (item.upper[1] !== -1 ? item.upper[1] : '~') : `${item.upper[1]} ${t('이하')}`}</span>
                              </div>
                              <hr />
                              <div className='guage-item-bottom'>
                                <span>{item.level !== 3 ? (item.lower[0] !== -1 ? item.lower[0] : '~') : `${item.lower[0]} ${t('미만')}`}</span>
                                {item.level !== 0 && item.level !== 3 && <span>~</span>}
                                <span>{item.level !== 0 ? (item.lower[1] !== -1 ? item.lower[1] : '~') : `${item.lower[1]} ${t('이상')}`}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className='range-right'>
                          <span>(% vol)</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    /** 센서범위 지정값이 있는경우만 표시 */
                    sensorRange && (
                      <div className='guide levels'>
                        <div className='value level0 flex-basic'>
                          <div className='flex-col'>
                            <span className='value-title'>{t('좋음')}</span>
                            <span>
                              {sensorRange.level1} {t('미만')}
                            </span>
                          </div>
                        </div>
                        <div className='value level1 flex-basic'>
                          <div className='flex-col'>
                            <span className='value-title'>{t('주의')}</span>
                            <span>
                              {sensorRange.level1} {t('이상')}
                            </span>
                          </div>
                        </div>
                        <div className='value level2 flex-basic'>
                          <div className='flex-col'>
                            <span className='value-title'>{t('경고')}</span>
                            <span>
                              {sensorRange.level2} {t('이상')}
                            </span>
                          </div>
                        </div>
                        <div className='value level3 flex-basic'>
                          <div className='flex-col'>
                            <span className='value-title'>{t('위험')}</span>
                            <span>
                              {sensorRange.level3} {t('이상')}
                            </span>
                          </div>
                        </div>
                      </div>
                    )
                  )}
                </div>
              )}
              <div className='flex-basic iconBtnGroup'>
                <BtnGhost onClick={() => onClickChart({ tableData: subSensor, type: 'lineChart2' })}>
                  <span className='material-symbols-rounded'>monitoring</span>
                  {t('차트')}
                </BtnGhost>
                {renderExcelBtn(onClickExcelSave2)}
                {/* {renderPrintBtn(onClickPrint2)} */}
              </div>
            </div>
          </div>
        </SearchOptions>
        {isLoadingSubSensor || isFetchingSubSensor ? (
          <LoadingModalBackground>
            <PulseLoader className='flex-center' color='rgb(0, 122, 255)' size='1rem' style={{ width: 'inherit', height: 'inherit' }} />
          </LoadingModalBackground>
        ) : subSensor?.length > 0 ? (
          <TuiGridTable tableState={subSensor} columns={subColumns} listCount={Number(listCount[LIST_COUNT])} pageNum={1} gridId='grid1' />
        ) : (
          <div className='emptyData'>
            <img src={nodataIllustrator} alt='noData' />
            <span>{t('선택하신 해당 일자의 데이터가 존재하지 않습니다')}</span>
          </div>
        )}

        <div className='hide'>
          <TuiGridTable
            rowKey={rowState.rowKey}
            tableState={exportTableState}
            columns={exportColumns}
            listCount={Number(listCount[LIST_COUNT])}
            setExcelBtn={setExcelBtn2}
            excelBtn={excelBtn2}
            fileName={getFileName('sub')}
          />
        </div>
      </Root>
    );
  };

  const loadingMain = isLoadingMainSensor || isFetchingMainSensor;

  // 메인센서 데이터 조회 경우에는 기본적인 레이아웃은 보여지게 작업
  // if (isErrorMainSensor) return <IssueGuide />;

  return (
    <div className='contents'>
      <Tab tabList={newTabList || tabInfo.tabList} currentTabMrCd={currentTabMrCd} setCurrentTabMrCd={setCurrentTabMrCd} onClickTab={onClickTab} size={size} />
      <div className={`content-container ${size.innerSize.W > 1024 ? 'twoColumn column-55 mediaSet768' : 'oneColumn'}`}>
        <Root loading={loadingMain} className={size.innerSize.W > 1024 || !viewRowState ? 'showRoot' : 'hideRoot'}>
          <SearchOptions>
            {size.innerSize.W > 1024 ? (
              <>
                <div className='inputsWrapper'>
                  <div className='inputForm-col withLabelComCf'>
                    <label htmlFor='fName'>{t('장비')}</label>
                    <SelectBox
                      options={fNameList}
                      defaultOption={t('전체')}
                      state={fName}
                      setState={setFName}
                      stateKey='fName'
                      filterbar='filter-1-left'
                      initiateKey={fName.fName}
                      optionHeight='height-md'
                    />
                  </div>
                  <div className='inputForm-row-fit'>
                    <span className='calendarLabel'>{t('조회일자')}</span>
                    <div className='inputForm-col'>
                      <div className='flex-basic'>
                        <input id='startInput' type='text' value={searchOption.start} onChange={(e) => onChangeRangeInput(e, 'start', setRangeState, setSearchOption)} />
                        <span className='inputDash'> ~ </span>
                        <input id='endInput' type='text' value={searchOption.end} onChange={(e) => onChangeRangeInput(e, 'end', setRangeState, setSearchOption)} />
                      </div>
                      {visibleRangePicker && (
                        <div className='rangePickerWrapper'>
                          <RangePicker state={rangeState} setState={setRangeState} setVisible={setVisibleRangePicker} />
                        </div>
                      )}
                    </div>
                    <button type='button' className='date-picker-btn' onClick={onClickRangeInput}>
                      <span className='material-symbols-rounded'>calendar_month</span>
                    </button>
                  </div>
                </div>
                <div className='inputsWrapper'>
                  <div className='secondSearchOption'>
                    <div className='flex-basic textBtnGroup'>
                      <ShortcutButton icon='search' buttonText='검색' shortcut='F9' onClick={onClickSearch} />
                      <BtnGhost onClick={onClickInitiateSearchOption}>{t('초기화')}</BtnGhost>
                      <div className='searchResult'>
                        {t('총')}
                        <span>{mainSensor?.length || 0}</span>
                        {t('개')}
                      </div>
                      <div className='inputForm-row'>
                        <div className='inputForm-col withLabelComCf'>
                          <label htmlFor='useYn'>{t('보기 설정')}</label>
                          <SelectBox
                            options={listCountComCdList}
                            defaultOption={listCount.cdName}
                            state={listCount}
                            setState={setListCount}
                            stateKey={LIST_COUNT}
                            initiateKey={listCount[LIST_COUNT]}
                            setTableLinesAPI={patchUserMenuAPI}
                            optionHeight='height-sm'
                            rsearch
                          />
                        </div>
                      </div>
                    </div>

                    <div className='flex-basic iconBtnGroup'>
                      <BtnGhost onClick={() => onClickChart({ tableData: mainSensor, type: 'lineChart' })}>
                        <span className='material-symbols-rounded'>monitoring</span>
                        {t('차트')}
                      </BtnGhost>
                      {renderExcelBtn(onClickExcelSave)}
                      {/* {renderPrintBtn(onClickPrint)} */}
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className='filter-wrapper'>
                <div className='inputsWrapper'>
                  <div className='flex-basic iconBtnGroup'>
                    <BtnGhost onClick={onClickFilter} className='filter'>
                      <span className='material-symbols-rounded'>filter_alt</span>
                      {t('검색 필터')}
                    </BtnGhost>
                    <BtnGhost onClick={() => onClickChart({ tableData: mainSensor, type: 'lineChart' })}>
                      <span className='material-symbols-rounded'>monitoring</span>
                      {t('차트')}
                    </BtnGhost>
                    {renderExcelBtn(onClickExcelSave)}
                  </div>
                </div>
              </div>
            )}
          </SearchOptions>
          {loadingMain ? (
            <LoadingModalBackground>
              <PulseLoader className='flex-center' color='rgb(0, 122, 255)' size='1rem' style={{ width: 'inherit', height: 'inherit' }} />
            </LoadingModalBackground>
          ) : isErrorMainSensor ? (
            <IssueGuide />
          ) : mainSensor?.length > 0 ? (
            <TuiGridTable
              rowKey={rowState.rowKey}
              tableState={mainSensor}
              columns={mainColumns}
              listCount={Number(listCount[LIST_COUNT])}
              setExcelBtn={setExcelBtn}
              excelBtn={excelBtn}
              onClickRow={onClickRow}
              fileName={getFileName('main')}
              pageNum={1}
              gridId='grid2'
            />
          ) : (
            <div className='emptyData'>
              <img src={nodataIllustrator} alt='noData' />
              <span>{t('해당 일자의 데이터가 존재하지 않습니다')}</span>
            </div>
          )}
        </Root>
        {renderSubTable()}
      </div>
      <Portal openModal={openModal?.status}>
        {(openModal?.type === 'lineChart' || openModal?.type === 'lineChart2') && (
          <LineChartModal
            setOpenModal={setOpenModal}
            chartData={openModal.type === 'lineChart' ? mainChartDataReverse : subChartDataReverse}
            datasets={openModal.type === 'lineChart' ? mainDataSet : subDataSet}
            title={t(`${mGasInfo.name} 측정기`)}
          />
        )}
      </Portal>
      {size.innerSize.W <= 1024 && (
        <Portal openModal>
          <SearchFilterModal openModal={openModal} setOpenModal={setOpenModal} onClickSearch={onClickSearch} onClickReset={onClickInitiateSearchOption}>
            <div className='filter-option-wrapper'>
              <div className='filter-option-col filter-underline filter-option-date'>
                <span className='calendarLabel'>{t('조회일자')}</span>
                <div className='inputForm-col'>
                  <div className='flex-basic' role='presentation' onClick={onClickRangeInput}>
                    <input id='startInput' type='text' readOnly value={searchOption.start} onChange={(e) => onChangeRangeInput(e, 'start', setRangeState, setSearchOption)} />
                    <span className='inputDash'> ~ </span>
                    <input id='endInput' type='text' readOnly value={searchOption.end} onChange={(e) => onChangeRangeInput(e, 'end', setRangeState, setSearchOption)} />
                  </div>
                  {visibleRangePicker && (
                    <div className='rangePickerWrapper'>
                      <RangePicker state={rangeState} setState={setRangeState} setVisible={setVisibleRangePicker} />
                    </div>
                  )}
                </div>
              </div>
              <div className='filter-option-cont'>
                <div className='filter-option-row'>
                  <label htmlFor='useYn'>{t('보기 설정')}</label>
                  <SelectBox
                    options={listCountComCdList}
                    defaultOption={listCount.cdName}
                    state={listCount}
                    setState={setListCount}
                    stateKey={LIST_COUNT}
                    initiateKey={listCount[LIST_COUNT]}
                    setTableLinesAPI={patchUserMenuAPI}
                    optionHeight='height-sm'
                    rsearch
                  />
                </div>
                <div className='filter-option-row'>
                  <label htmlFor='fName'>{t('장비')}</label>
                  <SelectBox
                    options={fNameList}
                    defaultOption={t('전체')}
                    state={fName}
                    setState={setFName}
                    stateKey='fName'
                    filterbar='filter-1-left'
                    initiateKey={fName.fName}
                    optionHeight='height-md'
                  />
                </div>
              </div>
            </div>
          </SearchFilterModal>
        </Portal>
      )}
    </div>
  );
};

export default Ssensor8;
